import { Stack, SxProps, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { Link, SvgIcon, type FC, type iconKeys } from ".."

export type TagProps = {
  label: string
  title?: string
  color?: string
  icon?: string
  autoWidth?: boolean
  hasBorder?: boolean
  uri?: string
  sx?: SxProps
}

const Tag: FC<TagProps> = ({
  label,
  title = "",
  color = "white",
  icon = null,
  hasBorder = false,
  autoWidth = false,
  uri = null,
  sx,
}) => {
  return (
    <Stack
      display="inline-flex"
      position="relative"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      gap={1}
      px={3}
      py={4}
      borderRadius={9999}
      height={30}
      minWidth={autoWidth ? "unset" : 80}
      sx={[
        (theme) => ({
          transition: theme.transitions.create(["background-color", "box-shadow", "border-color"]),
          textTransform: "capitalize",
        }),
        (theme) => {
          switch (color) {
            case "orange":
              return {
                bgcolor: theme.palette["primary.200"].main,
                color: theme.palette["primary.900"].main,
                ...(uri && {
                  "&:hover, &:focus-within": {
                    bgcolor: theme.palette["primary.300"].main,
                  },
                }),
              }
            case "blue":
              return {
                bgcolor: theme.palette["secondary.800"].main,
                color: theme.palette["neutral.100"].main,
                ...(uri && {
                  "&:hover, &:focus-within": {
                    bgcolor: theme.palette["secondary.900"].main,
                  },
                }),
                ...(hasBorder && {
                  border: `1px solid ${alpha(theme.palette["neutral.100"].main, 0.1)}}`,
                }),
              }
            case "floralWhite":
              return {
                bgcolor: theme.palette["tertiary.200"].main,
                color: theme.palette["neutral.700"].main,
                ...(uri && {
                  "&:hover, &:focus-within": {
                    bgcolor: theme.palette["tertiary.300"].main,
                  },
                }),
                ...(hasBorder && {
                  border: `1px solid ${theme.palette["tertiary.300"].main}`,
                }),
              }
            case "white":
            default:
              return {
                bgcolor: theme.palette["neutral.100"].main,
                color: theme.palette["neutral.700"].main,

                ...(uri && {
                  "&:hover, &:focus-within": {
                    boxShadow: `1px 1px 4px 0px ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
                  },
                }),
                ...(hasBorder && {
                  border: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
                }),
              }
          }
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon && (
        <SvgIcon
          variant={icon as iconKeys}
          size={12}
          sx={{
            fill: (theme) => theme.palette["neutral.300"].main,
          }}
        />
      )}
      <Typography variant="link.xs" >{label || title}</Typography>
      {uri && (
        <Link
          type="overlay"
          href={uri}
          aria-label={label || title}
          sx={{ "&:focus": { outline: 0 } }}
          zIndex={1}
        />
      )}
    </Stack>
  )
}
export default Tag
